import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import api, { API } from "../lib/api";
import VerdictBadge from "../components/VerdictBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, CheckCircle2, Download, ArrowLeft } from "lucide-react";
import ExpertReviewCTA from "../components/ExpertReviewCTA";

function useCountUp(target, ms = 900) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (target == null) return;
    const start = performance.now();
    let raf;
    const loop = (t) => {
      const p = Math.min((t - start) / ms, 1);
      setN(Math.round(target * (0.5 - Math.cos(Math.PI * p) / 2)));
      if (p < 1) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return n;
}

const scoreColor = (v) => v == null ? "text-zinc-400" : v >= 75 ? "text-emerald-700" : v >= 45 ? "text-amber-700" : "text-red-700";

export default function Result() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const shown = useCountUp(data?.final_score);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/cos/${id}`);
        setData(data);
      } catch (e) {
        setErr("Unable to load this check.");
      }
    })();
  }, [id]);

  if (err) return <div className="p-10 text-center text-red-700">{err}</div>;
  if (!data) return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-6 py-16 text-zinc-500 font-mono text-sm">Loading…</div>
    </div>
  );

  const info = data.metadata?.info || {};
  const struct = data.metadata?.structure || {};
  const indicators = data.metadata?.text_indicators || {};
  const allRedFlags = [
    ...(data.rule_red_flags || []).map((t) => ({ t, src: "RULE" })),
    ...(data.ai_red_flags || []).map((t) => ({ t, src: "AI" })),
  ];

  const reportUrl = `${API}/cos/${id}/report`;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/app/history" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900" data-testid="result-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to history
        </Link>

        <ExpertReviewCTA checkId={id} />

        {/* Verdict header */}
        <div className="mt-6 border border-zinc-200 bg-white rounded-sm">
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-5 p-8 border-b lg:border-b-0 lg:border-r border-zinc-200">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Genuinity score</div>
              <div className={`score-number text-8xl lg:text-9xl mt-3 ${scoreColor(data.final_score)}`} data-testid="result-score">
                {shown}
              </div>
              <div className="font-mono text-xs text-zinc-500 mt-1">out of 100</div>
              <div className="mt-6">
                <VerdictBadge verdict={data.final_verdict} size="lg" data-testid="result-verdict-badge" />
              </div>
            </div>

            <div className="lg:col-span-7 p-8">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">File</div>
                  <div className="font-mono text-sm mt-1 truncate max-w-[420px]">{data.filename}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-4">Analysed</div>
                  <div className="font-mono text-sm mt-1">{new Date(data.created_at).toLocaleString()}</div>
                </div>
                <a href={reportUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-sm" data-testid="result-download-report">
                    <Download className="w-4 h-4" /> Download PDF report
                  </Button>
                </a>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-4">
                <ScoreCell label="Rule score" value={data.rule_score} />
                <ScoreCell label="AI score" value={data.ai_score} />
                <ScoreCell label="AI confidence" value={data.ai_confidence} suffix="%" />
              </div>

              <div className="mt-6 border-t border-zinc-200 pt-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">AI reasoning</div>
                <p className="text-sm text-zinc-800 mt-2 leading-relaxed" data-testid="result-ai-reasoning">
                  {data.ai_reasoning || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="mt-6">
          <Tabs defaultValue="flags" className="w-full">
            <TabsList className="rounded-sm bg-zinc-100 p-1">
              <TabsTrigger value="flags" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-flags">Flags</TabsTrigger>
              <TabsTrigger value="metadata" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-metadata">PDF Metadata</TabsTrigger>
              <TabsTrigger value="text" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-text">Text Indicators</TabsTrigger>
            </TabsList>

            <TabsContent value="flags" className="mt-4">
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="border border-red-200 bg-white rounded-sm">
                  <div className="px-5 py-3 border-b border-red-200 bg-red-50 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-700" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-900">Red flags · {allRedFlags.length}</div>
                  </div>
                  <ul className="p-5 space-y-3" data-testid="result-red-flags">
                    {allRedFlags.length === 0 && <li className="text-sm text-zinc-500">No red flags detected.</li>}
                    {allRedFlags.map((f, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm border border-zinc-300 text-zinc-600">{f.src}</span>
                        <span className="text-zinc-800">{f.t}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border border-emerald-200 bg-white rounded-sm">
                  <div className="px-5 py-3 border-b border-emerald-200 bg-emerald-50 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-900">Positive indicators · {(data.rule_green_flags || []).length}</div>
                  </div>
                  <ul className="p-5 space-y-3" data-testid="result-green-flags">
                    {(!data.rule_green_flags || data.rule_green_flags.length === 0) && <li className="text-sm text-zinc-500">No positive indicators.</li>}
                    {(data.rule_green_flags || []).map((f, i) => (
                      <li key={i} className="text-sm text-zinc-800">• {f}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="metadata" className="mt-4">
              <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0F172A] text-white">
                    <tr>
                      <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em]">Field</th>
                      <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em]">Value</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-xs" data-testid="result-metadata-table">
                    {Object.entries({ ...info, ...struct }).map(([k, v]) => (
                      <tr key={k} className="border-b border-zinc-100 last:border-0">
                        <td className="px-4 py-2 text-zinc-500 w-1/3">{k}</td>
                        <td className="px-4 py-2 text-zinc-900 break-words">{v === null || v === "" ? "—" : String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="text" className="mt-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["cos_wording_present", "'Certificate of Sponsorship' text"],
                  ["tier_category_present", "'Tier and Category' section"],
                  ["current_status_present", "'Current certificate status' field"],
                  ["migrant_section_present", "'Migrant' section detected"],
                ].map(([k, label]) => (
                  <div key={k} className="border border-zinc-200 bg-white rounded-sm p-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
                    <div className={`font-heading text-2xl font-bold mt-1 ${indicators[k] ? "text-emerald-700" : "text-red-700"}`}>
                      {indicators[k] ? "PRESENT" : "MISSING"}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function ScoreCell({ label, value, suffix = "" }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`font-mono text-2xl font-bold mt-1 ${scoreColor(value)}`}>
        {value == null ? "—" : `${value}${suffix}`}
      </div>
    </div>
  );
}
